import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { RegionResponse, UtilityStateResponse } from "src/app/interfaces/responses/models";
import { RegionService } from "src/app/services/region.service";
import { UtilityService } from "src/app/services/utility.service";
import { ToastService } from "src/app/services/toast.service";
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { StatusDialogComponent } from '../utilities-add/utilities-add.component';
import { stringifyBooleans, toBoolean } from 'src/app/shared/utils/form-utils';
import { BaseComponent } from "src/app/shared/base.component";
import { takeUntil, switchMap } from "rxjs/operators";
import { of } from 'rxjs';

@Component({
    selector: "app-utilities-edit",
    templateUrl: "./utilities-edit.component.html",
    styleUrls: ["./utilities-edit.component.scss"],
})
export class UtilitiesEditComponent extends BaseComponent implements OnInit {
    utilityForm!: FormGroup;
    states: RegionResponse[] = [];
    logoPreviewUrl: string | ArrayBuffer | null = null;
    submitted = false;
    utilityId: string;

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<UtilitiesEditComponent>,
        private dialog: MatDialog,
        private utilityService: UtilityService,
        private regionService: RegionService,
        private toastService: ToastService,
        @Inject(MAT_DIALOG_DATA) public data: { utility: UtilityStateResponse }
    ) {
        super();
        this.utilityId = data.utility.id;
        this.initForm();
    }

    private initForm(): void {
        const u = this.data.utility;
        this.utilityForm = this.fb.group({
            utilityName: [u.name, [Validators.required, Validators.pattern("^[a-zA-Z\\s]+$")]],
            description: [u.description, Validators.pattern("^[a-zA-Z\\s]*$")],
            email: [u.email, [Validators.required, Validators.email]],
            enterState: ["", Validators.required],
            phoneNumber: [u.phone_number, [Validators.required, Validators.pattern("^[0-9]{10}$")]],
            enterLocality: [u.address, Validators.pattern("^[a-zA-Z0-9\\s]*$")],
            selectStatus: [toBoolean(u.active) ? "Active" : "Retired", Validators.required],
            enterPincode: [u.pin_code, [Validators.required, Validators.pattern("^[0-9]{6}$")]],
            utilityLogo: [null],
        });
    }

    ngOnInit(): void {
        this.regionService.getStates()
            .pipe(takeUntil(this.destroy$))
            .subscribe(response => {
                this.states = response.items;
                const stateId = this.findStateId(this.data.utility.state);
                this.utilityForm.patchValue({ enterState: stateId });
            });
    }

    private findStateId(stateName: string): string {
        if (!stateName) return '';
        const found = this.states.find(s => s.name.toLowerCase() === stateName.toLowerCase());
        return found ? found.id : stateName;
    }

    onCancel(): void {
        if (this.utilityForm.dirty) {
            const confirmRef = this.dialog.open(ConfirmDialogComponent, {
                panelClass: 'custom-dialog-container',
                data: {
                    title: 'Confirmation',
                    message: 'You have unsaved changes. Are you sure you want to discard them?'
                }
            });

            confirmRef.afterClosed()
                .pipe(takeUntil(this.destroy$))
                .subscribe(result => {
                    if (result) this.dialogRef.close();
                });
        } else {
            this.dialogRef.close();
        }
    }

    onSubmit(): void {
        this.submitted = true;
        if (this.utilityForm.invalid) {
            this.toastService.show("Please fill all required fields correctly.", "error");
            return;
        }

        const formData = this.utilityForm.value;
        const payload = stringifyBooleans({
            name: formData.utilityName,
            description: formData.description,
            state: formData.enterState,
            phone_number: formData.phoneNumber,
            pin_code: formData.enterPincode,
            address: formData.enterLocality,
            email: formData.email,
            active: formData.selectStatus === 'Active'
        });

        this.utilityService.updateUtility(this.utilityId, payload)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(response => {
                    const logoFile = formData.utilityLogo;
                    if (logoFile) {
                        return this.utilityService.uploadUtilityLogo(this.utilityId, logoFile).pipe(
                            switchMap(() => of(response))
                        );
                    }
                    return of(response);
                })
            )
            .subscribe({
                next: () => {
                    this.toastService.show("Utility updated successfully!");
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    console.error("[UtilitiesEdit] Error:", err);
                    this.toastService.show(err.error?.message || "An unexpected error occurred.", "error");
                }
            });
    }

    onFileSelected(event: any): void {
        const file: File = event.target.files[0];
        if (file) {
            this.utilityForm.patchValue({ utilityLogo: file });
            this.utilityForm.markAsDirty();
            const reader = new FileReader();
            reader.onload = () => { this.logoPreviewUrl = reader.result; };
            reader.readAsDataURL(file);
        }
    }

    openStatusDialog(): void {
        const statusRef = this.dialog.open(StatusDialogComponent, {
            width: "500px",
            panelClass: "custom-dialog-container",
            disableClose: true,
        });

        statusRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.utilityForm.patchValue({ selectStatus: result.status });
                    this.utilityForm.markAsDirty();
                }
            });
    }

    get f() { return this.utilityForm.controls; }
    get utilityName() { return this.utilityForm.get('utilityName'); }
    get description() { return this.utilityForm.get('description'); }
    get email() { return this.utilityForm.get('email'); }
    get enterState() { return this.utilityForm.get('enterState'); }
    get phoneNumber() { return this.utilityForm.get('phoneNumber'); }
    get enterLocality() { return this.utilityForm.get('enterLocality'); }
    get statusControl() { return this.utilityForm.get('selectStatus'); }
    get enterPincode() { return this.utilityForm.get('enterPincode'); }
}
